<script>

    import 'flatpickr/dist/flatpickr.css'
    import { Spanish } from 'flatpickr/dist/l10n/es.js'
    import Moment from 'moment'
    import Flatpickr from 'svelte-flatpickr'
    import { createEventDispatcher } from 'svelte'

    export let dateStart = Moment().startOf('month').startOf('day').toDate()
    export let dateEnd = Moment().endOf('month').endOf('day').toDate()

    const dispatch = createEventDispatcher()

    let openPicker = null

    $: pickerValue = openPicker === 'start' ? dateStart : dateEnd
    $: pickerOptions = {
        inline: true,
        enableTime: false,
        locale: Spanish,
        minDate: openPicker === 'end' ? dateStart : null,
        maxDate: openPicker === 'start' ? dateEnd : null,
        defaultDate: pickerValue,
    }

    function formatDate(date) {
        return Moment(date).locale('es').format('D MMM YYYY')
    }

    function emitChange() {
        dispatch('change', { dateStart, dateEnd })
    }

    function togglePicker(which) {
        openPicker = openPicker === which ? null : which
    }

    function shiftMonth(delta) {
        const month = Moment(dateStart).add(delta, 'month')
        dateStart = month.clone().startOf('month').startOf('day').toDate()
        dateEnd = month.clone().endOf('month').endOf('day').toDate()
        openPicker = null
        emitChange()
    }

    function onPickerChange(ev) {
        const picked = ev.detail?.[0]
        if (!picked) return

        if (openPicker === 'start') {
            dateStart = Moment(picked).startOf('day').toDate()
            if (Moment(dateStart).isAfter(dateEnd))
                dateEnd = Moment(dateStart).endOf('day').toDate()
        } else {
            dateEnd = Moment(picked).endOf('day').toDate()
            if (Moment(dateEnd).isBefore(dateStart))
                dateStart = Moment(dateEnd).startOf('day').toDate()
        }

        openPicker = null
        emitChange()
    }

    function clickOutside(node, onClose) {
        const onClick = (event) => {
            if (!node.contains(event.target) && !event.target.closest('.date-range-nav__date'))
                onClose()
        }

        setTimeout(() => document.addEventListener('click', onClick), 0)

        return {
            destroy() {
                document.removeEventListener('click', onClick)
            },
        }
    }

</script>

<style>
    .date-range-nav {
        display: inline-flex;
        align-items: center;
        gap: 0.65rem;
        background: #fff;
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        padding: 0.55rem 0.85rem;
        color: #111827;
        line-height: 1;
        user-select: none;
    }

    .date-range-nav__icon {
        color: #9ca3af;
        font-size: 0.85rem;
    }

    .date-range-nav__arrow {
        background: none;
        border: none;
        cursor: pointer;
        color: #9ca3af;
        font-size: 0.75rem;
        line-height: 1;
        padding: 0.2rem;
        border-radius: 6px;
        transition: background 0.15s ease, color 0.15s ease;
    }

    .date-range-nav__arrow:hover {
        background: #f3f4f6;
        color: #111827;
    }

    .date-range-nav__dates {
        position: relative;
        display: inline-flex;
        align-items: center;
        gap: 0.45rem;
    }

    .date-range-nav__date {
        background: none;
        border: none;
        cursor: pointer;
        padding: 0.15rem 0.25rem;
        border-radius: 6px;
        font-size: 0.875rem;
        font-weight: 600;
        color: #111827;
        text-transform: capitalize;
        line-height: 1;
        transition: background 0.15s ease;
    }

    .date-range-nav__date:hover,
    .date-range-nav__date.is-active {
        background: #f3f4f6;
    }

    .date-range-nav__sep {
        font-size: 0.875rem;
        font-weight: 600;
        color: #9ca3af;
    }

    .date-range-nav__popover {
        position: absolute;
        top: calc(100% + 10px);
        left: 50%;
        transform: translateX(-50%);
        z-index: 20;
        background: #fff;
        border: 1px solid #e8e8e8;
        border-radius: 10px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
        overflow: hidden;
    }

    .date-range-nav__popover :global(.flatpickr-input) {
        display: none;
    }

    .date-range-nav__popover :global(.flatpickr-calendar) {
        box-shadow: none;
    }
</style>

<div class="date-range-nav">
    <button type="button" class="date-range-nav__arrow" aria-label="Mes anterior" on:click={() => shiftMonth(-1)}>
        <i class="fas fa-chevron-left"></i>
    </button>

    <i class="far fa-calendar date-range-nav__icon"></i>

    <div class="date-range-nav__dates">
        <button
            type="button"
            class="date-range-nav__date"
            class:is-active={openPicker === 'start'}
            on:click|stopPropagation={() => togglePicker('start')}
        >
            { formatDate(dateStart) }
        </button>

        <span class="date-range-nav__sep">-</span>

        <button
            type="button"
            class="date-range-nav__date"
            class:is-active={openPicker === 'end'}
            on:click|stopPropagation={() => togglePicker('end')}
        >
            { formatDate(dateEnd) }
        </button>

        {#if openPicker}
            <div class="date-range-nav__popover" use:clickOutside={() => openPicker = null}>
                {#key openPicker}
                    <Flatpickr value={pickerValue} options={pickerOptions} on:change={onPickerChange} />
                {/key}
            </div>
        {/if}
    </div>

    <button type="button" class="date-range-nav__arrow" aria-label="Mes siguiente" on:click={() => shiftMonth(1)}>
        <i class="fas fa-chevron-right"></i>
    </button>
</div>
